import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Progress,
  Spinner,
  Alert,
} from '@/components/ui-compat'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import {
  faUpload,
  faFileImage,
  faFilePdf,
  faCheckCircle,
  faTimes,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import { toastr } from 'react-redux-toastr'
import { useUpload, formatBytes } from '../../../../components/file-manager/hooks/useUpload'
import * as Mutation from '../../../../api/gql/mutation'

interface BlueprintUploadModalProps {
  isOpen: boolean
  onClose: () => void
  floorId: string
  floorName?: string
  onSuccess?: () => void
}

type UploadStage = 'preview' | 'uploading' | 'complete' | 'error'

const BlueprintUploadModal: React.FC<BlueprintUploadModalProps> = ({
  isOpen,
  onClose,
  floorId,
  floorName,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // State
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [stage, setStage] = useState<UploadStage>('preview')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Upload hook
  const [upload, uploadState] = useUpload() as any
  const { uploads } = uploadState

  // Mutations
  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [setFloorBlueprint] = useMutation(Mutation.SET_FLOOR_BLUEPRINT)

  // Get current upload progress
  const uploadProgress = Object.values(uploads)[0] as any

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null)
      setPreviewUrl(null)
      setStage('preview')
      setErrorMessage(null)
    }
  }, [isOpen])

  // Generate preview URL when file is selected
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }
  }, [selectedFile])

  const handleFileSelect = useCallback((file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      toastr.error('', t('Snags_upload_blueprint_invalid_type'))
      return
    }
    setSelectedFile(file)
    setStage('preview')
    setErrorMessage(null)
  }, [t])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleUpload = async () => {
    if (!selectedFile || !floorId) return

    setStage('uploading')
    setErrorMessage(null)

    try {
      // Step 1: Create file record and get signed URL
      const { data: createFileData } = await createFile({
        variables: {
          input: {
            filename: selectedFile.name,
            size: selectedFile.size,
            mimetype: selectedFile.type || 'application/octet-stream',
            encoding: 'utf8',
            directory: `blueprints/${floorId}`,
          },
        },
      })

      const fileId = createFileData.createFile.file.id
      const signedUrl = createFileData.createFile.uploadUrl

      // Step 2: Upload to S3
      await upload([
        {
          file: selectedFile,
          id: fileId,
          name: selectedFile.name,
          signedUrl,
        },
      ])

      // Step 3: Mark file as uploaded
      await setFileUploaded({
        variables: { ids: [fileId] },
      })

      // Step 4: Associate with floor (backend auto-triggers DZI processing)
      await setFloorBlueprint({
        variables: { floorId, fileId },
      })

      // Done — DZI processing happens automatically on the backend
      setStage('complete')
    } catch (error: any) {
      console.error('Blueprint upload error:', error)
      setStage('error')
      setErrorMessage(error.message || t('Snags_blueprint_upload_failed'))
    }
  }

  const handleClose = () => {
    if (stage === 'uploading') {
      return
    }
    onClose()
  }

  const isPdf = selectedFile?.type === 'application/pdf'

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={stage !== 'uploading' ? handleClose : undefined}>
        {t('Snags_upload_blueprint_title')}
        {floorName && <small className="text-muted-foreground ml-2">— {floorName}</small>}
      </ModalHeader>
      <ModalBody>
        {/* File Selection / Drop Zone */}
        {!selectedFile ? (
          <div
            className="border-2 border-dashed rounded p-5 text-center"
            style={{
              borderColor: '#dee2e6',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              minHeight: 250,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div
              className="rounded-full bg-white flex items-center justify-center mb-3 shadow-sm"
              style={{ width: 80, height: 80 }}
            >
              <Icon icon={faUpload as any} className="text-primary" style={{ fontSize: '2rem' }} />
            </div>
            <h5 className="font-bold mb-2">{t('Snags_upload_blueprint_drop_hint')}</h5>
            <p className="text-muted-foreground mb-0 small">{t('Snags_upload_blueprint_supported')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
        ) : (
          <>
            {/* Preview Section */}
            <div className="mb-4">
              <div className="flex items-start">
                {/* Preview Image/Icon */}
                <div
                  className="rounded border bg-muted flex items-center justify-center mr-3 overflow-hidden"
                  style={{ width: 120, height: 120, flexShrink: 0 }}
                >
                  {isPdf ? (
                    <Icon icon={faFilePdf as any} className="text-destructive" style={{ fontSize: '3rem' }} />
                  ) : previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Icon icon={faFileImage as any} className="text-muted-foreground" style={{ fontSize: '3rem' }} />
                  )}
                </div>

                {/* File Info */}
                <div className="flex-grow">
                  <h6 className="font-bold mb-1 truncate" style={{ maxWidth: 400 }}>
                    {selectedFile.name}
                  </h6>
                  <p className="text-muted-foreground mb-2 small">
                    {formatBytes(selectedFile.size)} • {selectedFile.type || t('Snags_unknown_file_type')}
                  </p>
                </div>

                {/* Remove button */}
                {stage === 'preview' && (
                  <Button
                    color="light"
                    size="sm"
                    className="ml-2"
                    onClick={() => {
                      setSelectedFile(null)
                      setPreviewUrl(null)
                    }}
                  >
                    <Icon icon={faTimes as any} />
                  </Button>
                )}
              </div>
            </div>

            {/* Upload Progress */}
            {stage === 'uploading' && (
              <div className="mb-3">
                <div className="flex justify-between mb-2">
                  <span className="font-bold">{t('Snags_upload_blueprint_uploading')}</span>
                  <span className="text-muted-foreground">
                    {uploadProgress?.loaded || '0 Bytes'} / {uploadProgress?.total || formatBytes(selectedFile.size)}
                  </span>
                </div>
                <Progress
                  value={uploadProgress?.progress || 0}
                  color="primary"
                  animated
                  striped
                  style={{ height: 10 }}
                />
              </div>
            )}

            {/* Success Status */}
            {stage === 'complete' && (
              <div className="text-center py-4">
                <Icon
                  icon={faCheckCircle as any}
                  className="text-[hsl(var(--success))] mb-3"
                  style={{ fontSize: '3rem' }}
                />
                <p className="font-bold mb-0">{t('Snags_upload_blueprint_success')}</p>
              </div>
            )}

            {/* Error Status */}
            {stage === 'error' && errorMessage && (
              <Alert color="danger" className="mb-0">
                <Icon icon={faExclamationTriangle as any} className="mr-2" />
                {errorMessage}
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {stage === 'preview' && (
          <>
            <Button color="secondary" onClick={handleClose}>
              {t('Common_cancel')}
            </Button>
            <Button
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile}
              className="font-bold"
            >
              <Icon icon={faUpload as any} className="mr-2" />
              {t('Snags_upload_blueprint_button')}
            </Button>
          </>
        )}
        {stage === 'error' && (
          <>
            <Button color="secondary" onClick={handleClose}>
              {t('Common_cancel')}
            </Button>
            <Button color="primary" onClick={handleUpload}>
              {t('Common_retry')}
            </Button>
          </>
        )}
        {stage === 'uploading' && (
          <Button color="secondary" disabled>
            <Spinner size="sm" className="mr-2" />
            {t('Snags_upload_blueprint_uploading')}
          </Button>
        )}
        {stage === 'complete' && (
          <Button color="success" onClick={() => { onSuccess?.(); onClose(); }}>
            {t('Common_done', 'Done')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default BlueprintUploadModal
